import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Layout } from "../components/Layout";
import { api } from "../api/client";
import { REMOVAL_REASONS, removalReasonLabel } from "../lib/tenants";

export function RemoveTenantPage() {
  const { id } = useParams();
  const tenantId = Number(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [reason, setReason] = useState("");
  const [evictionDate, setEvictionDate] = useState(new Date().toISOString().slice(0, 10));
  const [amountOutstanding, setAmountOutstanding] = useState("");
  const [reasonNotes, setReasonNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  const tenantQ = useQuery({
    queryKey: ["tenant", tenantId],
    queryFn: () => api.tenants.get(tenantId),
    enabled: Number.isInteger(tenantId),
  });

  const removeMut = useMutation({
    mutationFn: () =>
      api.tenants.remove(tenantId, {
        reason,
        evictionDate: reason === "eviction" ? evictionDate : null,
        amountOutstanding:
          reason === "eviction" && amountOutstanding !== "" ? Number(amountOutstanding) : null,
        reasonNotes: reasonNotes.trim() || null,
      }),
    onSuccess: ({ evictionListed }) => {
      queryClient.invalidateQueries({ queryKey: ["tenants"] });
      queryClient.invalidateQueries({ queryKey: ["tenant", tenantId] });
      navigate(evictionListed ? "/eviction-list" : "/tenants");
    },
    onError: () => setError("Could not remove the tenant. Please try again."),
  });

  const tenant = tenantQ.data?.tenant;

  function onConfirm() {
    setError(null);
    if (!reason) return setError("Please choose a reason for removing this tenant.");
    removeMut.mutate();
  }

  if (tenantQ.isLoading) {
    return (
      <Layout title="Remove Tenant">
        <p className="sub">Loading…</p>
      </Layout>
    );
  }
  if (!tenant) {
    return (
      <Layout title="Remove Tenant">
        <section className="card">
          <p>That tenant could not be found.</p>
          <Link to="/tenants" className="btn btn-ghost btn-xl">
            Back to Tenants
          </Link>
        </section>
      </Layout>
    );
  }

  const isEviction = reason === "eviction";

  return (
    <Layout title="Remove a Tenant">
      <p className="help-line">
        Removing a tenant keeps their history. Please choose the reason.{" "}
        <strong>Eviction</strong> also adds this person to the org-wide eviction list.
      </p>
      <section className="card form-card">
        <h2>
          Remove tenant: {tenant.firstName} {tenant.lastName}
        </h2>
        <p className="sub">
          {tenant.where} · {tenant.buildingName}
        </p>

        <fieldset className="choice-set">
          {REMOVAL_REASONS.map((r) => (
            <label key={r} className={"choice" + (reason === r ? " selected" : "")}>
              <input
                type="radio"
                name="reason"
                checked={reason === r}
                onChange={() => setReason(r)}
              />
              <span>{removalReasonLabel(r)}</span>
            </label>
          ))}
        </fieldset>

        {isEviction && (
          <>
            <label className="field">
              <span>Eviction date</span>
              <input
                type="date"
                value={evictionDate}
                onChange={(e) => setEvictionDate(e.target.value)}
              />
            </label>
            <label className="field">
              <span>Amount outstanding</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={amountOutstanding}
                onChange={(e) => setAmountOutstanding(e.target.value)}
              />
            </label>
            <div className="warn-box">
              <strong>⚠ This person will be added to the eviction list.</strong>
              They will be blocked from being added as a tenant again at any building.
            </div>
          </>
        )}

        <label className="field">
          <span>Reason / notes</span>
          <textarea
            rows={3}
            value={reasonNotes}
            onChange={(e) => setReasonNotes(e.target.value)}
            placeholder={isEviction ? "Details of the eviction…" : "Optional details…"}
          />
        </label>

        {error && <p className="field-error">{error}</p>}

        <div className="form-actions">
          <button
            className="btn btn-ghost btn-xl"
            onClick={() => navigate(`/tenants/${tenantId}`)}
            disabled={removeMut.isPending}
          >
            Cancel
          </button>
          <button
            className="btn btn-danger btn-xl"
            onClick={onConfirm}
            disabled={removeMut.isPending}
          >
            {removeMut.isPending
              ? "Removing…"
              : isEviction
                ? "Remove & add to eviction list"
                : "Remove tenant"}
          </button>
        </div>
      </section>
    </Layout>
  );
}
